/**
 * Form Render Functions
 *
 * Explicit render functions for different control types.
 * Replaces generic groupControls.map with granular control functions.
 *
 * Each function accepts a control configuration with:
 * - label: Field label
 * - required: Whether field is required
 * - placeholder: Placeholder text
 * - visibility: Boolean flag to show/hide field
 * - disabled: Boolean flag to disable field
 */

import * as React from 'react'
import { Button } from '@/components/ui/button'
import { AppLogger } from '@/lib/logger'
import type { Logger } from '@/lib/logger'
import { useImageConfigParser } from '@/hooks/useImageConfigParser'
import { CropDialog } from '@/lib/upload/CropDialog'
import { validateImageFile } from '@/lib/imageUploadUtils'
import type { CropResult } from '@/lib/imageUploadTypes'
import { useToast } from '@/contexts/ToastContext'

// ============================================================================
// Logger (Lazy Initialization)
// ============================================================================

let _log: Logger | null = null

function getLog(): Logger {
  if (!_log) {
    _log = AppLogger.getLogger('forms/formRenderFunctions')
  }
  return _log
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface ControlConfig {
  name: string
  label: string
  placeholder?: string
  required?: boolean
  readonly?: boolean
  visibility?: boolean
  disabled?: boolean
  description?: string
  type?: 'text' | 'email' | 'password' | 'number' | 'date' | 'file' | 'tel'
  options?: Array<{ value: string; label: string; description?: string }>
  // Optional raw select options from YAML (some composers keep them around)
  selectOptions?: unknown
  isLoading?: boolean
  htmlControl?: string
  // Uploader configuration for file uploads (when htmlControl === 'file')
  uploader?: any
}

export interface ButtonConfig {
  label?: string
  variant?: 'default' | 'outline' | 'secondary' | 'destructive' | 'ghost'
  type?: 'button' | 'submit'
  onClick?: () => void
  disabled?: boolean
  visibility?: boolean
}

// ============================================================================
// Type for Bound Field Context
// ============================================================================

type BoundFieldContext = {
  TextField: React.ComponentType<any>
  SelectField: React.ComponentType<any>
  DatePickerField: React.ComponentType<any>
  CheckboxField: React.ComponentType<any>
  RadioGroupField: React.ComponentType<any>
  TextareaField: React.ComponentType<any>
  MediaUploaderField: React.ComponentType<any>
}

type SubmitButtonComponent = {
  SubmitButton?: React.ComponentType<any>
}

// ============================================================================
// Render Functions
// ============================================================================

/**
 * Render a text input control
 *
 * @param control - Control configuration
 * @param bound - Bound field context from form field
 * @returns JSX element or null if not visible
 */
export function renderTextControl(
  control: ControlConfig,
  bound: BoundFieldContext
): React.ReactNode {
  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  const { TextField } = bound

  return (
    <TextField
      type={control.type || 'text'}
      label={control.label}
      required={control.required ?? false}
      placeholder={control.placeholder || ''}
      disabled={(control.disabled ?? false) || (control.readonly ?? false)}
      description={control.description}
    />
  )
}

/**
 * Render a number input control
 *
 * @param control - Control configuration
 * @param bound - Bound field context from form field
 * @returns JSX element or null if not visible
 */
export function renderNumberControl(
  control: ControlConfig,
  bound: BoundFieldContext
): React.ReactNode {
  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  const { TextField } = bound

  return (
    <TextField
      type="number"
      label={control.label}
      required={control.required ?? false}
      placeholder={control.placeholder || ''}
      disabled={(control.disabled ?? false) || (control.readonly ?? false)}
      description={control.description}
    />
  )
}

/**
 * Render a textarea control
 *
 * @param control - Control configuration
 * @param bound - Bound field context from form field
 * @returns JSX element or null if not visible
 */
export function renderTextareaControl(
  control: ControlConfig,
  bound: BoundFieldContext
): React.ReactNode {
  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  const { TextareaField } = bound

  return (
    <TextareaField
      label={control.label}
      required={control.required ?? false}
      placeholder={control.placeholder || ''}
      disabled={control.disabled ?? false}
      description={control.description}
      rows={4}
    />
  )
}

/**
 * Render a select control
 *
 * @param control - Control configuration
 * @param bound - Bound field context from form field
 * @returns JSX element or null if not visible
 */
export function renderSelectControl(
  control: ControlConfig,
  bound: BoundFieldContext
): React.ReactNode {
  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  const { SelectField } = bound

  return (
    <SelectField
      label={control.label}
      required={control.required ?? false}
      placeholder={control.isLoading ? 'Loading...' : control.placeholder || ''}
      options={control.options || []}
      description={control.description}
      disabled={(control.disabled ?? false) || (control.readonly ?? false)}
    />
  )
}

/**
 * Render a date picker control
 *
 * @param control - Control configuration
 * @param bound - Bound field context from form field
 * @returns JSX element or null if not visible
 */
export function renderDateControl(
  control: ControlConfig,
  bound: BoundFieldContext
): React.ReactNode {
  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  const { DatePickerField } = bound

  return (
    <DatePickerField
      label={control.label}
      required={control.required ?? false}
      placeholder={control.placeholder || ''}
      disabled={control.disabled ?? false}
      description={control.description}
    />
  )
}

/**
 * Render a checkbox control
 *
 * @param control - Control configuration
 * @param bound - Bound field context from form field
 * @returns JSX element or null if not visible
 */
export function renderCheckboxControl(
  control: ControlConfig,
  bound: BoundFieldContext
): React.ReactNode {
  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  const { CheckboxField } = bound

  return (
    <CheckboxField
      label={control.label}
      required={control.required ?? false}
      disabled={control.disabled ?? false}
      description={control.description}
    />
  )
}

/**
 * Render a radio group control
 *
 * @param control - Control configuration
 * @param bound - Bound field context from form field
 * @returns JSX element or null if not visible
 */
export function renderRadioControl(
  control: ControlConfig,
  bound: BoundFieldContext
): React.ReactNode {
  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  const { RadioGroupField } = bound

  return (
    <RadioGroupField
      label={control.label}
      required={control.required ?? false}
      options={control.options || []}
      disabled={control.disabled ?? false}
      description={control.description}
    />
  )
}

/**
 * Render a media uploader control
 *
 * @param control - Control configuration with uploader property
 * @param bound - Bound field context from form field
 * @param entityId - Entity ID for upload (default: "new")
 * @param mockMode - Enable mock mode for development/testing
 * @returns JSX element or null if not visible
 */
export function renderMediaUploaderControl(
  control: ControlConfig,
  bound: BoundFieldContext,
  entityId: string = 'new',
  mockMode?: boolean
): React.ReactNode {
  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  // Check for uploader config
  if (!control.uploader) {
    getLog().warn(`No uploader config found for control: ${control.name}`)
    return null
  }

  const { MediaUploaderField } = bound

  return (
    <MediaUploaderField
      config={control.uploader}
      entityId={entityId}
      label={control.label}
      required={control.required ?? false}
      disabled={control.disabled ?? false}
      description={control.description}
      mockMode={mockMode}
    />
  )
}

/**
 * Render an image upload control
 *
 * Integrates image-specific validation (magic bytes, per-type size limits),
 * optional client-side cropping, and compression metadata passthrough.
 * Follows the same pattern as renderMediaUploaderControl.
 *
 * @param control - Control configuration with uploader property
 * @param bound - Bound field context from form field
 * @param entityId - Entity ID for upload (default: "new")
 * @param tier - Optional tier for config override (e.g., 'free', 'pro')
 * @returns JSX element or null if not visible
 */
function ImageUploadControlRenderer({
  control,
  bound,
  entityId = 'new',
  tier,
}: {
  control: ControlConfig
  bound: BoundFieldContext
  entityId?: string
  tier?: string
}): React.ReactNode {
  const log = getLog()
  const { toast } = useToast()

  // Parse and resolve config with tier overrides
  const config = useImageConfigParser(control.uploader, tier)
  const { MediaUploaderField } = bound
  const [cropDialogOpen, setCropDialogOpen] = React.useState(false)
  const [fileToCrop, setFileToCrop] = React.useState<File | null>(null)
  const uploaderRef = React.useRef<any>(null)

  // Wrap the onFilesChange callback to add validation and cropping
  const handleFilesChange = React.useCallback(
    async (files: any[]) => {
      if (!config) return

      log.debug(`Image files changed: ${files.length} files`)

      // Validate each file
      const validFiles = []
      for (const fileItem of files) {
        const file = fileItem.file || fileItem
        const result = await validateImageFile(file, config)

        if (!result.valid) {
          log.warn(`File validation failed: ${file.name} - ${result.error?.code}`)
          toast({
            title: 'File Validation Error',
            description: result.error?.message || 'File validation failed',
            type: 'error',
            duration: 5000,
          })
          // Don't include this file in the list
        } else {
          validFiles.push(fileItem)

          // Check if cropping is needed
          if (config.dimensions?.aspectRatios && config.dimensions.aspectRatios.length > 0) {
            log.debug(`Showing crop dialog for: ${file.name}`)
            setFileToCrop(file)
            setCropDialogOpen(true)
          }
        }
      }

      // Update the file list with only valid files
      if (uploaderRef.current) {
        // Notify the parent component of changes
        // This will be handled through the MediaUploaderField's internal state
      }
    },
    [config, log, toast]
  )

  // Handle crop dialog apply
  const handleCropApply = React.useCallback(
    (cropResult: CropResult) => {
      if (!fileToCrop) return

      log.debug(`Crop applied: ${cropResult.width}x${cropResult.height}`)

      // Update the file in the uploader
      // Since we're using MediaUploaderField, the files will be in its state
      // We need to notify it of the file replacement

      setCropDialogOpen(false)
      setFileToCrop(null)

      toast({
        title: 'Image Cropped',
        description: `Image cropped to ${cropResult.width}×${cropResult.height}px`,
        type: 'success',
        duration: 3000,
      })
    },
    [fileToCrop, log, toast]
  )

  const handleCropCancel = React.useCallback(() => {
    log.debug('Crop dialog cancelled')
    setCropDialogOpen(false)
    setFileToCrop(null)
  }, [log])

  const [hasFile, setHasFile] = React.useState(false)

  const handleFilesChangeWithRemove = React.useCallback(
    async (files: any[]) => {
      setHasFile(files.length > 0)
      await handleFilesChange(files)
    },
    [handleFilesChange]
  )

  const handleRemove = React.useCallback(() => {
    if (uploaderRef.current?.clearFiles) {
      uploaderRef.current.clearFiles()
    }
    setHasFile(false)
    log.debug('Image removed by user')
    toast({ title: 'Image Removed', description: 'The uploaded image has been removed.', type: 'default', duration: 2000 })
  }, [log, toast])

  // Respect visibility flag
  if (control.visibility === false) {
    return null
  }

  // Check for uploader config
  if (!control.uploader) {
    log.warn(`No uploader config found for control: ${control.name}`)
    return null
  }

  if (!config) {
    log.warn(`Failed to resolve image upload config for control: ${control.name}`)
    return null
  }

  return (
    <>
      <MediaUploaderField
        ref={uploaderRef}
        config={control.uploader}
        entityId={entityId}
        label={control.label}
        required={control.required ?? false}
        disabled={control.disabled ?? false}
        description={control.description}
        onFilesChange={handleFilesChangeWithRemove}
      />

      {hasFile && !control.disabled && (
        <button
          type="button"
          onClick={handleRemove}
          className="mt-2 flex items-center gap-1.5 text-sm text-destructive hover:text-destructive/80 transition-colors"
          aria-label="Remove uploaded image"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14H6L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4h6v2" />
          </svg>
          Remove image
        </button>
      )}

      {fileToCrop && config.dimensions && (
        <CropDialog
          isOpen={cropDialogOpen}
          file={fileToCrop}
          config={config.dimensions}
          onApply={handleCropApply}
          onCancel={handleCropCancel}
        />
      )}
    </>
  )
}

export function renderImageUploadControl(
  control: ControlConfig,
  bound: BoundFieldContext,
  entityId: string = 'new',
  tier?: string
): React.ReactNode {
  if (control.visibility === false) {
    return null
  }

  if (!control.uploader) {
    return null
  }

  return (
    <ImageUploadControlRenderer
      control={control}
      bound={bound}
      entityId={entityId}
      tier={tier}
    />
  )
}

/**
 * Render a button control
 *
 * Handles button variants (Save/Primary vs Cancel/Secondary)
 * Integrates with form validation to manage disabled state.
 *
 * @param config - Button configuration
 * @param components - Optional SubmitButton component from form instance
 * @returns JSX element or null
 */
export function renderButtonControl(
  config: ButtonConfig,
  components?: SubmitButtonComponent
): React.ReactNode {
  if (config.visibility === false) {
    return null
  }

  const label = config.label ?? ''

  // If it's a submit button and we have the SubmitButton component (from app-form), use it
  if (config.type === 'submit' && components?.SubmitButton) {
    const { SubmitButton } = components
    return (
      <SubmitButton
        label={label || 'Submit'}
        variant={config.variant || 'default'}
      />
    )
  }

  // Otherwise use the imported Button component from shadcn
  return (
    <Button
      type={config.type || 'button'}
      variant={config.variant || 'default'}
      onClick={config.onClick}
      disabled={config.disabled}
      className="h-11 text-base font-semibold px-6"
      size="lg"
    >
      {label}
    </Button>
  )
}

// ============================================================================
// Helper Function: Determine Control Type and Render
// ============================================================================

/**
 * Universal control renderer that determines type and delegates to specific renderer
 *
 * @param control - Control configuration from form.yml
 * @param bound - Bound field context from form field
 * @param entityId - Entity ID for file uploads (optional)
 * @param tier - Optional tier for config override (optional)
 * @param mockMode - Enable mock mode for uploads (optional)
 * @returns JSX element or null
 */
export function renderControl(
  control: ControlConfig,
  bound: BoundFieldContext,
  entityId?: string,
  tier?: string,
  mockMode?: boolean
): React.ReactNode {
  // Handle visibility at the top level
  if (control.visibility === false) {
    return null
  }

  // Handle image upload control
  if (control.htmlControl === 'imageUpload') {
    return renderImageUploadControl(control, bound, entityId, tier)
  }

  // Handle file control with media uploader
  if (control.htmlControl === 'file') {
    return renderMediaUploaderControl(control, bound, entityId, mockMode)
  }

  // Dispatch to specific renderer based on control type
  if (control.htmlControl === 'checkbox') {
    return renderCheckboxControl(control, bound)
  }

  if (control.htmlControl === 'radio') {
    return renderRadioControl(control, bound)
  }

  if (control.htmlControl === 'textarea') {
    return renderTextareaControl(control, bound)
  }

  if (control.options && control.options.length > 0) {
    return renderSelectControl(control, bound)
  }

  if (control.type === 'date') {
    return renderDateControl(control, bound)
  }

  if (control.type === 'number') {
    return renderNumberControl(control, bound)
  }

  return renderTextControl(control, bound)
}
